import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit , signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { AccountService } from 'src/app/services/account/account.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { AccountDialogComponent } from '../modals/account-dialog/account-dialog.component';
import { SubscriptionsDialogComponent } from '../modals/subscriptions-dialog/subscriptions-dialog.component';
import { HistoryDialogComponent } from '../modals/history-dialog/history-dialog.component';
import { SettingsDialogComponent } from '../modals/settings-dialog/settings-dialog.component';
import { WelcomeComponent } from '../modals/info/welcome/welcome.component';
import { FeedbackComponent } from '../modals/info/feedback/feedback.component';
import { TutorialComponent } from '../modals/info/tutorial/tutorial.component';
import { PrivacyPolicyComponent } from '../modals/info/privacy-policy/privacy-policy.component';
import { EulaComponent } from '../modals/info/eula/eula.component';

@Component({
  standalone: true,
  selector: 'app-profile-dropdown',
  templateUrl: './profile-dropdown.component.html',
  styleUrls: ['./profile-dropdown.component.scss'],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileDropdownComponent implements OnInit, OnDestroy {
  @Input() loadingMap: string = "";
  @Input() dropdownVisible: boolean = false;
  public readonly isAuthorized = signal<boolean>(false);
  public readonly infoSubMenuVisible = signal<boolean>(false);

  public readonly loginIconPath = signal<string>('');
  public readonly editIconPath = signal<string>('');
  public readonly subscriptionsIconPath = signal<string>('');
  public readonly historyIconPath = signal<string>('');
  public readonly settingsIconPath = signal<string>('');
  public readonly infoIconPath = signal<string>('');
  public readonly openArrowIconPath = signal<string>('');
  public readonly logoutIconPath = signal<string>('');
  public readonly backMenuIconPath = signal<string>('');
  public readonly welcomeIconPath = signal<string>('');
  public readonly feedbackIconPath = signal<string>('');
  public readonly tutorialIconPath = signal<string>('');
  public readonly privacyPolicyIconPath = signal<string>('');
  public readonly legalInfoIconPath = signal<string>('');

  private subscriptions: Subscription = new Subscription();

  constructor(
    public settingsService: SettingsService,
    public languageService: LanguageService,
    private stackedModalsService: StackedModalsService,
    private accountService: AccountService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.settingsService.ObservableMap.subscribe((mapSettings) => {
      if (mapSettings) {
        const skin = mapSettings.Skin;
        const iconBase = `/assets/icons/${skin}/ui`;

        this.loginIconPath.set(`${iconBase}/login_icon.svg`);
        this.editIconPath.set(`${iconBase}/edit_icon.svg`);
        this.subscriptionsIconPath.set(`${iconBase}/menu_icon_subscriptions.svg`);
        this.historyIconPath.set(`${iconBase}/menu_icon_history.svg`);
        this.settingsIconPath.set(`${iconBase}/settings_icon.svg`);
        this.infoIconPath.set(`${iconBase}/info_icon.svg`);
        this.openArrowIconPath.set(`${iconBase}/open_arrow.svg`);
        this.logoutIconPath.set(`${iconBase}/logout_icon.svg`);
        this.backMenuIconPath.set(`/assets/icons/light/ui/back_menu.svg`);
        this.welcomeIconPath.set(`${iconBase}/welcome_icon.svg`);
        this.feedbackIconPath.set(`${iconBase}/feedback_icon.svg`);
        this.tutorialIconPath.set(`${iconBase}/tutorial_icon.svg`);
        this.privacyPolicyIconPath.set(`${iconBase}/privacy_policy_icon.svg`);
        this.legalInfoIconPath.set(`${iconBase}/legal_info_icon.svg`);
      }
    }));

    this.subscriptions.add(this.accountService.ObservableIsAuthorized.subscribe((resp) => {
      if (resp != null && resp != undefined) {
        this.isAuthorized.set(resp);
      }
    }));

  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public openSubscriptionsDialog(): void {
    this.stackedModalsService.openModal(SubscriptionsDialogComponent, {
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  public openAccountDialog(): void {
    const process = this.accountService.getIsAuthorized() ? 'profileEdit' : 'signIn';
    this.stackedModalsService.openModal(AccountDialogComponent, {
      data: { process: process },
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  public openHistoryDialog(): void {
    this.stackedModalsService.openModal(HistoryDialogComponent, {
      panelClass: 'dialog-snap-to-fullscreen',
      autoFocus: false
    });
  }

  public openSettings(): void {
    this.stackedModalsService.openModal(SettingsDialogComponent, {
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  public toggleInfoSubMenu(): void {
    this.infoSubMenuVisible.update(visible => !visible);
  }

  public closeInfoSubMenu(): void {
    this.infoSubMenuVisible.set(false);
  }

  public openInfoTab(tab: string): void {
    const componentMap: { [key: string]: any } = {
      welcome: WelcomeComponent,
      feedback: FeedbackComponent,
      tutorial: TutorialComponent,
      privacy: PrivacyPolicyComponent,
      legal: EulaComponent
    };
    const component = componentMap[tab];
    if (!component) return;
    this.stackedModalsService.openModal(component, {
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  public logout(): void {
    this.accountService.logout();
  }

  public login(): void {
    this.stackedModalsService.openModal(AccountDialogComponent, {
      data: { process: 'signIn' },
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }
}